import { useNavigate, useSearchParams } from 'react-router-dom'
import { useSubscription } from '../context/SubscriptionContext'
import { useState, useEffect, useRef, useMemo } from 'react'
import { subscriptionService, deliveryService, authService } from '../services'
import { PlanDetails, DeliverySchedule, Subscription, PlanItem } from '../types/api'
import {
  HeroSection,
  FeaturesSection,
  FruitsSection,
  HowItWorksSection,
  OurServicesSection,
  SubscriptionPlansSection,
  SendAsGiftSection,
  CustomerReviewsSection,
  Footer
} from '../components/home'
import ServiceSwitcher from '../components/ServiceSwitcher'
import InstagramEmbed from '../components/common/InstagramEmbed'
import apiClient from '../services/api'
import axios from 'axios'
import { useTranslation } from 'react-i18next'
import ShutdownNotice from '../components/MaintenanceNotice'

// localStorage key for promotion code
const PROMO_CODE_STORAGE_KEY = 'piika_promo_code';

export default function Flowers() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { subscriptions, isLoading: isSubscriptionLoading, error: subscriptionError, renewSubscription, upgradeSubscription } = useSubscription();
  const [planDetails, setPlanDetails] = useState<PlanItem[]>([]);
  const [upcomingDeliveries, setUpcomingDeliveries] = useState<DeliverySchedule[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [promoInfo, setPromoInfo] = useState<string | null>(null);
  const [promoFetching, setPromoFetching] = useState(false);
  const deliveriesFetchedRef = useRef(false);
  const plansLoadedRef = useRef(false);
  const isInitialMount = useRef(true);
  const [reviewFilter, setReviewFilter] = useState<'all' | 'with-photos' | 'five-star' | 'premium' | 'standard'>('all');
  const [expandedPhotoIndex, setExpandedPhotoIndex] = useState<number | null>(null);
  const [reviews, setReviews] = useState<Array<{
    id: string;
    userName: string;
    userInitials: string;
    planType: 'premium' | 'standard';
    date: string;
    rating: number;
    text: string;
    photos: string[];
    helpfulCount: number;
  }>>([]);
  
  // Fetch promotion code details from API
  const fetchPromoDetails = async (code: string) => {
    if (!code || promoFetching) return;
    
    try {
      setPromoFetching(true);
      const response = await fetch(`/api/promotion-codes/${code}`);
      const data = await response.json() as {
        isValid: boolean;
        percentOff?: number;
        amountOff?: number;
        description?: string;
        error?: string;
      };
      
      if (data.isValid) {
        // Set promotion info based on API response
        if (data.percentOff && data.percentOff > 0) {
          setPromoInfo(`${data.percentOff}% OFF`);
        } else if (data.amountOff && data.amountOff > 0) {
          setPromoInfo(`฿${data.amountOff} OFF`);
        } else if (data.description) {
          setPromoInfo(data.description);
        }
      } else {
        // If promo is invalid, clear it from localStorage
        localStorage.removeItem(PROMO_CODE_STORAGE_KEY);
        console.warn('Invalid promotion code:', data.error);
      }
    } catch (err) {
      console.error('Error fetching promotion details:', err);
    } finally {
      setPromoFetching(false);
    }
  };
  
  // Extract promotion code from URL parameters
  useEffect(() => {
    const promoCode = searchParams.get('promo');
    if (promoCode) {
      // Save the promotion code to localStorage
      localStorage.setItem(PROMO_CODE_STORAGE_KEY, promoCode);
      console.log('Promotion code saved to localStorage:', promoCode);
      // Fetch promo details
      fetchPromoDetails(promoCode);
    } else {
      // Check if there's a promo code in localStorage
      const savedPromoCode = localStorage.getItem(PROMO_CODE_STORAGE_KEY);
      if (savedPromoCode) {
        fetchPromoDetails(savedPromoCode);
      }
    }
  }, [searchParams]);

  // Fetch subscription plans and upcoming deliveries
  useEffect(() => {
    console.log('Home useEffect triggered. isSubscriptionLoading:', isSubscriptionLoading, 'subscriptions.length:', subscriptions.length);
    
    const fetchData = async () => {
      // Skip if this is not the initial mount or if already fetched
      if (!isInitialMount.current || (plansLoadedRef.current && deliveriesFetchedRef.current)) {
        console.log('Home: Skipping fetch - not initial mount or already fetched');
        return;
      }
      
      // Ensure we don't fetch again if already loading
      if (isLoading) {
        console.log('Home: Skipping fetch - already loading');
        return;
      }
      
      console.log('Home: Starting fetch. isLoading:', isLoading, 'plansLoaded:', plansLoadedRef.current, 'deliveriesFetched:', deliveriesFetchedRef.current);
      setIsLoading(true);
      setError(null);
      
      try {
        // Always fetch plans - even for non-authenticated users
        if (!plansLoadedRef.current) {
          console.log('Home: Fetching subscription plans...');
          const plans = await subscriptionService.getPlans();
          console.log('Home: Got plans from API');
          setPlanDetails(plans);
          plansLoadedRef.current = true;
        }
        
        // Only fetch deliveries for authenticated users with subscriptions
        if (authService.isAuthenticated() && subscriptions.length > 0 && !deliveriesFetchedRef.current) {
          console.log('Home: Fetching upcoming deliveries...');
          // Pass existing subscriptions to avoid duplicate API call
          const deliveries = await deliveryService.getUpcomingDeliveries(subscriptions);
          console.log('Home: Got deliveries from API');
          setUpcomingDeliveries(deliveries);
          deliveriesFetchedRef.current = true;
        } else if (!authService.isAuthenticated()) {
          // Mark deliveries as fetched for non-authenticated users
          deliveriesFetchedRef.current = true;
          console.log('Home: User not authenticated, skipping deliveries fetch');
        }
        
        // Fetch reviews from the API - anyone can see reviews, logged in or not
        try {
          // Create a separate non-authenticated API instance for public data
          const publicEndpoint = axios.create({
            baseURL: apiClient.defaults.baseURL,
            headers: {
              'Content-Type': 'application/json',
            }
          });
          
          const response = await publicEndpoint.get('/reviews');
          if (response.data && response.data.data) {
            setReviews(response.data.data);
          }
        } catch (reviewError) {
          console.error('Error fetching reviews:', reviewError);
          // Don't set an error state for reviews as they're not critical
        }
      } catch (err) {
        console.error('Error fetching plans:', err);
        setError(t('flowers.errors.loadSubscriptionData'));
      } finally {
        setIsLoading(false);
        isInitialMount.current = false;
      }
    };
    
    // Only fetch data if subscriptions finished loading, to avoid race conditions
    if (!isSubscriptionLoading) {
      fetchData();
    }
    
  }, [isSubscriptionLoading, t]);

  // Handle selecting a subscription plan
  const handlePlanSelect = (plan: 'standard' | 'premium') => {
    // Get the saved promotion code from localStorage
    const savedPromoCode = localStorage.getItem(PROMO_CODE_STORAGE_KEY);
    
    // If authenticated, proceed with subscription flow
    navigate('/subscription', { 
      state: { 
        selectedPlan: plan,
        fromPage: 'home',
        serviceType: 'flower',  // Set service type to flower
        giftType: null,  // Explicitly not a gift
        isGift: false,   // Extra property to ensure it's not treated as a gift
        clearGiftMode: true,  // Explicitly clear any existing gift mode
        // Include the promotion code in the navigation state if available
        promotionCode: savedPromoCode || undefined
      } 
    });
  };

  // Determine the combined loading state
  const isPageLoading = isSubscriptionLoading || isLoading;

  // Add a function to filter reviews
  const filteredReviews = useMemo(() => {
    switch(reviewFilter) {
      case 'five-star':
        return reviews.filter(review => review.rating === 5);
      case 'premium':
        return reviews.filter(review => review.planType === 'premium');
      case 'standard':
        return reviews.filter(review => review.planType === 'standard');
      default:
        return reviews;
    }
  }, [reviews, reviewFilter]);

  // Function to mark review as helpful
  const markReviewAsHelpful = (reviewId: string) => {
    setReviews(reviews.map(review => 
      review.id === reviewId
        ? { ...review, helpfulCount: review.helpfulCount + 1 }
        : review
    ));
  };

  // Function to toggle photo modal
  const togglePhotoModal = (photoIndex: number | null) => {
    setExpandedPhotoIndex(photoIndex);
  };

  // Updated UI component with modern design and better mobile experience
  return (
    <div className="min-h-screen bg-white">
      {/* Accessibility skip link */}
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:p-4 focus:bg-white focus:z-50">
        {t('common.skipToMainContent')}
      </a>

      <ServiceSwitcher 
        isLoading={isPageLoading}
        subscriptions={subscriptions}
      />

      {/* Shutdown Notice */}
      <ShutdownNotice />

      <HeroSection 
        isLoading={isPageLoading}
        subscriptions={subscriptions}
        handlePlanSelect={handlePlanSelect}
        promoInfo={promoInfo}
        serviceType="flower"
      />

      {/* Choose Your Plan section - moved above Why Choose Piika? */}
      <div className="px-0">
        <SubscriptionPlansSection 
          isLoading={isPageLoading}
          planDetails={planDetails}
          handlePlanSelect={handlePlanSelect}
          promoInfo={promoInfo}
          serviceType="flower"
        />
      </div>

      {/* Send as Gift Section */}
      <SendAsGiftSection />

      <FeaturesSection />
      
      {/* Main Content - All sections are now visible */}
      <div id="main-content" className="px-0 bg-gray-50">
        {/* Display errors if any */}
        {(error || subscriptionError) && (
          <div className="bg-red-50 border border-red-200 text-gray-800 px-4 py-3 rounded-lg mb-6">
            <p className="font-medium">{t('common.error')}</p>
            <p className="text-sm">{error || subscriptionError}</p>
          </div>
        )}

        {/* Instagram Feed Section */}
        <div className="bg-white py-8">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-gray-800 mb-3">{t('common.instagram.followUs')}</h3>
              <p className="text-gray-600 text-md leading-relaxed">
                {t('common.instagram.gratitudeMessage')}<br/>
                {t('common.instagram.followDescription')}<br/>
                {t('common.instagram.handle')}
              </p>
            </div>
            
            <div className="overflow-x-auto overflow-y-hidden">
              <div className="flex gap-4 px-4 w-max">
                <div className="flex-shrink-0 w-80">
                  <InstagramEmbed
                    postUrl="https://www.instagram.com/p/DJRNzuhNmJq/"
                    captioned={true}
                    maxWidth={320}
                    className="w-full"
                  />
                </div>
                <div className="flex-shrink-0 w-80">
                  <InstagramEmbed
                    postUrl="https://www.instagram.com/p/DI7_MjCvhNh/"
                    captioned={true}
                    maxWidth={320}
                    className="w-full"
                  />
                </div>
                <div className="flex-shrink-0 w-4"></div>
              </div>
            </div>
          </div>
          
          {/* <div className="text-center mt-4">
            <a 
              href="https://www.instagram.com/piika.co/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-3 rounded-full font-medium text-sm hover:from-purple-600 hover:to-pink-600 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
              Follow @piika.co
            </a>
          </div> */}
        </div>

        <FruitsSection />

        <HowItWorksSection />
        
        {/* Upcoming Services Section - Temporarily hidden
        <div className="my-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">{t('flowers.comingSoonToPiika')}</h2>
          
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-6">
              <p className="text-gray-600 mb-6 text-center">
                {t('flowers.expandingServices')}
              </p>
              
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mb-2">
                    <img src={waterIcon} alt="Water" className="w-6 h-6" />
                  </div>
                  <p className="text-xs text-center font-medium">{t('flowers.bottledWater')}</p>
                </div>
                
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mb-2">
                    <img src={fruitIcon} alt="Fruits" className="w-6 h-6" />
                  </div>
                  <p className="text-xs text-center font-medium">{t('flowers.seasonalFruits')}</p>
                </div>
                
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mb-2">
                    <img src={snackIcon} alt="Snacks" className="w-6 h-6" />
                  </div>
                  <p className="text-xs text-center font-medium">{t('flowers.snackBoxes')}</p>
                </div>
              </div>
              
              <button 
                onClick={() => navigate('/upcoming-services')}
                className="w-full py-3 bg-yellow-400 text-center py-3.5 rounded-lg font-medium text-[#262626]"
              >
                {t('flowers.viewUpcomingServices')}
              </button>
            </div>
          </div>
        </div>
        */}
      </div>

      <OurServicesSection serviceType="flower" />

      {/* CustomerReviewsSection temporarily hidden
      <CustomerReviewsSection />
      */}

      <Footer />
    </div>
  )
}